import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Typography,
  Box,
  Chip,
  Alert,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormLabel,
  FormControl,
  Autocomplete,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Tooltip,
  Avatar,
  Skeleton,
  Fab,
  InputAdornment,
  Fade,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Repeat as RepeatIcon,
  Timer as TimerIcon,
  Visibility as VisibilityIcon,
  Directions as DirectionsIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import {
  interventiService,
  clientiService,
  cantieriService,
  usersService,
  veicoliService,
  timbratureService,
  presenzeService,
} from '../services/api';
import RapportinoModal from '../components/RapportinoModal';

// Componente StatBox riutilizzabile
const StatBox = ({ icon, value, label, color = '#e50000' }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      mb: 0.5
    }}>
      {icon && React.cloneElement(icon, { sx: { color: 'rgba(255,255,255,0.7)', fontSize: '1.2rem' } })}
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
      {label}
    </Typography>
  </Box>
);

const Interventi = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompact = useMediaQuery(theme.breakpoints.down('md')); // tablet/mobile: card invece di tabella larga
  const { user } = useSelector((state) => state.auth);
  const [interventi, setInterventi] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [capocantieri, setCapocantieri] = useState([]);
  const [tecnici, setTecnici] = useState([]);
  const [veicoli, setVeicoli] = useState([]);
  const [veicoliDisponibili, setVeicoliDisponibili] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentIntervento, setCurrentIntervento] = useState(null);
  const [interventoToDelete, setInterventoToDelete] = useState(null);
  const [error, setError] = useState('');
  const [isRicorrente, setIsRicorrente] = useState(false);
  const [duplicating, setDuplicating] = useState(false); // (A) copia da un intervento esistente
  const [filterCliente, setFilterCliente] = useState(null);
  const [filterCantiere, setFilterCantiere] = useState(null);
  const [filterCapocantiere, setFilterCapocantiere] = useState(null);
  const [filterTecnico, setFilterTecnico] = useState(null);
  const [filterOggi, setFilterOggi] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('data');
  // Compilazione rapportino inline (senza passare dal calendario)
  const [rapportinoModalOpen, setRapportinoModalOpen] = useState(false);
  const [rapportinoIntervento, setRapportinoIntervento] = useState(null);
  const [rapportinoTimbrature, setRapportinoTimbrature] = useState([]);
  const [rapportinoPresenze, setRapportinoPresenze] = useState([]);
  const [formData, setFormData] = useState({
    tipologia: 'manutenzione',
    cliente_id: '',
    cantiere_id: '',
    capocantiere_id: '',
    data: null,
    data_inizio: null,
    data_fine: null,
    ora_inizio: null,
    ora_fine: null,
    descrizione_lavori: '',
    note_operative: '',
    targa_mezzo: '',
    stato: 'programmato',
    tecnici_ids: [],
    giorni_settimana: [],
    ricorrente: false,
    abilita_timbrature_viaggio: true,
  });

  const giorniSettimana = [
    { value: 0, label: 'Lunedì' },
    { value: 1, label: 'Martedì' },
    { value: 2, label: 'Mercoledì' },
    { value: 3, label: 'Giovedì' },
    { value: 4, label: 'Venerdì' },
    { value: 5, label: 'Sabato' },
    { value: 6, label: 'Domenica' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  // Effetto per aprire automaticamente il dialog se si arriva dal calendario o dalla
  // pagina di dettaglio: editInterventoId apre in modifica, duplicateInterventoId apre
  // direttamente in modalità Duplica (stesso identico flusso del pulsante "Duplica").
  useEffect(() => {
    if (interventi.length === 0) return;

    if (location.state?.editInterventoId) {
      const interventoToEdit = interventi.find(i => i.id === location.state.editInterventoId);
      if (interventoToEdit) {
        handleOpenDialog(interventoToEdit);
        // Pulisce lo state per evitare riaperture indesiderate
        window.history.replaceState({}, document.title);
      }
    } else if (location.state?.duplicateInterventoId) {
      const interventoToDuplicate = interventi.find(i => i.id === location.state.duplicateInterventoId);
      if (interventoToDuplicate) {
        // Apre il dialog sui dati dell'intervento e poi passa in modalità Duplica,
        // come cliccare "Modifica" e poi "Duplica" dal calendario.
        (async () => {
          await handleOpenDialog(interventoToDuplicate);
          handleDuplica();
        })();
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, interventi]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [interventiRes, clientiRes, cantieriRes, capocRes, tecnRes, veicoliRes] = await Promise.all([
        interventiService.getAll(),
        clientiService.getAll(),
        cantieriService.getAll(),
        usersService.getCapocantieri(),
        usersService.getTecnici(),
        veicoliService.getAttivi(),
      ]);
      setInterventi(interventiRes.data);
      setClienti(clientiRes.data);
      setCantieri(cantieriRes.data);
      setCapocantieri(capocRes.data);
      setTecnici(tecnRes.data);
      setVeicoli(veicoliRes.data);
    } catch (error) {
      setError('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const loadVeicoliDisponibili = async (data, interventoId = null) => {
    try {
      if (!data) {
        setVeicoliDisponibili([]);
        return;
      }
      const response = await veicoliService.getDisponibili(data, interventoId);
      setVeicoliDisponibili(response.data);
    } catch (error) {
      console.error('Errore nel caricamento veicoli disponibili:', error);
      setVeicoliDisponibili([]);
    }
  };

  const handleOpenDialog = async (intervento = null) => {
    setDuplicating(false);
    if (intervento) {
      setCurrentIntervento(intervento);
      setIsRicorrente(false);
      setFormData({
        ...intervento,
        data: intervento.data ? dayjs(intervento.data) : null,
        ora_inizio: intervento.ora_inizio ? dayjs(`2000-01-01T${intervento.ora_inizio}`) : null,
        ora_fine: intervento.ora_fine ? dayjs(`2000-01-01T${intervento.ora_fine}`) : null,
        tecnici_ids: intervento.tecnici ? intervento.tecnici.map((t) => t.id) : [],
        ricorrente: false,
      });

      // Carica veicoli disponibili per la data dell'intervento
      if (intervento.data) {
        await loadVeicoliDisponibili(intervento.data, intervento.id);
      }
    } else {
      setCurrentIntervento(null);
      setIsRicorrente(false);
      setFormData({
        tipologia: 'manutenzione',
        cliente_id: '',
        cantiere_id: '',
        // Un capocantiere che crea un intervento parte con se stesso come capo (può cambiarlo)
        capocantiere_id: user?.role === 'capocantiere' ? user.id : '',
        data: null,
        data_inizio: null,
        data_fine: null,
        ora_inizio: null,
        ora_fine: null,
        descrizione_lavori: '',
        note_operative: '',
        targa_mezzo: '',
        stato: 'programmato',
        tecnici_ids: [],
        giorni_settimana: [],
        ricorrente: false,
        abilita_timbrature_viaggio: true,
      });
      setVeicoliDisponibili([]);
    }
    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentIntervento(null);
    setDuplicating(false);
    setError('');
  };

  // (A) Duplica: riapre lo stesso dialog come NUOVO intervento pre-compilato coi dati
  // dell'evento corrente. Tornando in modalità creazione ricompare il selettore di
  // ricorrenza (checkbox + giorni settimana), identico a quello della creazione.
  // L'intervento originale NON viene toccato.
  const handleDuplica = () => {
    setCurrentIntervento(null);   // modalità "nuovo": ricompare il chooser di ricorrenza
    setDuplicating(true);
    setIsRicorrente(false);
    setFormData((prev) => ({
      ...prev,
      id: undefined,
      ricorrenza_id: undefined,
      has_rapportino: false,
      stato: 'programmato',       // una copia nasce sempre programmata
      data: null,                 // nuova data da scegliere (evita un doppione lo stesso giorno)
      data_inizio: null,
      data_fine: null,
      giorni_settimana: [],
      ricorrente: false,
      targa_mezzo: '',            // il veicolo dipende dalla data: va riselezionato
    }));
    setVeicoliDisponibili([]);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Auto-compile cliente_id quando viene selezionato un cantiere
    if (name === 'cantiere_id' && value) {
      const selectedCantiere = cantieri.find(c => c.id === parseInt(value));
      if (selectedCantiere) {
        setFormData(prev => ({
          ...prev,
          cantiere_id: value,
          cliente_id: selectedCantiere.cliente_id
        }));
      }
    }
  };

  const handleCantiereChange = (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        cantiere_id: newValue.id,
        cliente_id: newValue.cliente_id
      });
    } else {
      setFormData({
        ...formData,
        cantiere_id: '',
        cliente_id: ''
      });
    }
  };

  const handleDateChange = (field) => async (value) => {
    setFormData({
      ...formData,
      [field]: value,
    });

    // Se la data è cambiata e non è ricorrente, carica veicoli disponibili
    if (field === 'data' && value && !isRicorrente) {
      const dataFormatted = value.format('YYYY-MM-DD');
      await loadVeicoliDisponibili(dataFormatted, currentIntervento?.id);
    }
  };

  const handleGiorniChange = (giorno) => {
    const giorni = [...formData.giorni_settimana];
    const index = giorni.indexOf(giorno);
    if (index > -1) {
      giorni.splice(index, 1);
    } else {
      giorni.push(giorno);
    }
    setFormData({
      ...formData,
      giorni_settimana: giorni,
    });
  };

  const handleSubmit = async () => {
    try {
      // Validazione minima: senza data/orari il backend andrebbe in errore 500.
      if (isRicorrente) {
        if (!formData.data_inizio || !formData.data_fine) {
          setError('Seleziona Data Inizio e Data Fine per la ricorrenza');
          return;
        }
        if (!formData.giorni_settimana || formData.giorni_settimana.length === 0) {
          setError('Seleziona almeno un giorno della settimana');
          return;
        }
      } else if (!formData.data) {
        setError('Seleziona la data dell\'intervento');
        return;
      }
      if (!formData.ora_inizio || !formData.ora_fine) {
        setError('Seleziona Ora Inizio e Ora Fine');
        return;
      }

      let dataToSend = {
        ...formData,
        ricorrente: isRicorrente,
      };

      if (isRicorrente) {
        dataToSend = {
          ...dataToSend,
          data_inizio: formData.data_inizio ? formData.data_inizio.format('YYYY-MM-DD') : null,
          data_fine: formData.data_fine ? formData.data_fine.format('YYYY-MM-DD') : null,
          ora_inizio: formData.ora_inizio ? formData.ora_inizio.format('HH:mm:ss') : null,
          ora_fine: formData.ora_fine ? formData.ora_fine.format('HH:mm:ss') : null,
        };
      } else {
        dataToSend = {
          ...dataToSend,
          data: formData.data ? formData.data.format('YYYY-MM-DD') : null,
          ora_inizio: formData.ora_inizio ? formData.ora_inizio.format('HH:mm:ss') : null,
          ora_fine: formData.ora_fine ? formData.ora_fine.format('HH:mm:ss') : null,
        };
      }

      if (currentIntervento) {
        await interventiService.update(currentIntervento.id, dataToSend);
      } else {
        await interventiService.create(dataToSend);
      }
      loadData();
      handleCloseDialog();
    } catch (error) {
      setError(error.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const handleOpenDeleteDialog = (intervento) => {
    setInterventoToDelete(intervento);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setInterventoToDelete(null);
  };

  const handleDelete = async () => {
    try {
      if (interventoToDelete.ricorrenza_id && window.confirm('Vuoi eliminare tutti gli interventi ricorrenti?')) {
        await interventiService.deleteRicorrenza(interventoToDelete.ricorrenza_id);
      } else {
        await interventiService.delete(interventoToDelete.id);
      }
      loadData();
      handleCloseDeleteDialog();
    } catch (error) {
      setError('Errore nell\'eliminazione dell\'intervento');
    }
  };

  const getStatoColor = (stato) => {
    const colors = {
      programmato: { bg: 'rgba(33, 150, 243, 0.1)', color: '#2196f3', border: '#2196f3' },
      in_corso: { bg: 'rgba(255, 152, 0, 0.1)', color: '#ff9800', border: '#ff9800' },
      completato: { bg: 'rgba(76, 175, 80, 0.1)', color: '#4caf50', border: '#4caf50' },
    };
    return colors[stato] || { bg: 'rgba(158, 158, 158, 0.1)', color: '#9e9e9e', border: '#9e9e9e' };
  };

  const getStatoIcon = (stato) => {
    switch (stato) {
      case 'programmato': return <CalendarIcon sx={{ fontSize: '0.9rem' }} />;
      case 'in_corso': return <PlayArrowIcon sx={{ fontSize: '0.9rem' }} />;
      case 'completato': return <CheckCircleIcon sx={{ fontSize: '0.9rem' }} />;
      default: return null;
    }
  };

  const getTipologiaStyle = (tipologia) => {
    return tipologia === 'manutenzione'
      ? { bg: 'rgba(229, 0, 0, 0.1)', color: '#e50000' }
      : { bg: 'rgba(156, 39, 176, 0.1)', color: '#9c27b0' };
  };

  const handleGetDirections = (intervento) => {
    // Coordinate sede aziendale NC Elettrica
    const sedeOrigin = '45.4761692,8.63074';

    // Usa l'indirizzo del cantiere come destinazione
    const destination = encodeURIComponent(intervento.cantiere_indirizzo || intervento.cantiere);

    // Apri Google Maps con indicazioni stradali
    const url = `https://www.google.com/maps/dir/?api=1&origin=${sedeOrigin}&destination=${destination}`;
    window.open(url, '_blank');
  };

  // Filtra interventi in base ai filtri selezionati
  const filteredInterventi = interventi.filter((intervento) => {
    // Filtro per ricerca testuale
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchCantiere = intervento.cantiere?.toLowerCase().includes(search);
      const matchCliente = intervento.cliente?.toLowerCase().includes(search);
      const matchCapocantiere = intervento.capocantiere?.toLowerCase().includes(search);
      const matchDescrizione = intervento.descrizione_lavori?.toLowerCase().includes(search);
      if (!matchCantiere && !matchCliente && !matchCapocantiere && !matchDescrizione) {
        return false;
      }
    }

    // Filtro per cliente
    if (filterCliente && intervento.cliente_id !== filterCliente.id) {
      return false;
    }

    // Filtro per cantiere
    if (filterCantiere && intervento.cantiere_id !== filterCantiere.id) {
      return false;
    }

    // Filtro per capocantiere
    if (filterCapocantiere && intervento.capocantiere_id !== filterCapocantiere.id) {
      return false;
    }

    // Filtro per tecnico assegnato
    if (filterTecnico && !(intervento.tecnici || []).some((t) => t.id === filterTecnico.id)) {
      return false;
    }

    // Filtro rapido "Oggi"
    if (filterOggi && dayjs(intervento.data).format('YYYY-MM-DD') !== dayjs().format('YYYY-MM-DD')) {
      return false;
    }

    return true;
  });

  // Reset pagina quando cambiano i filtri
  useEffect(() => {
    setPage(0);
  }, [searchTerm, filterCliente, filterCantiere, filterCapocantiere, filterTecnico, filterOggi]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Ordinamento per colonna (clic sull'intestazione: asc <-> desc)
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  // Valore confrontabile per ciascuna colonna ordinabile
  const getSortValue = (intervento, property) => {
    switch (property) {
      case 'data':
        return `${intervento.data || ''}T${intervento.ora_inizio || ''}`;
      case 'cliente':
        return (intervento.cliente || '').toLowerCase();
      case 'cantiere':
        return (intervento.cantiere || '').toLowerCase();
      case 'tipologia':
        return intervento.tipologia || '';
      case 'mezzo':
        return (intervento.targa_mezzo || '').toLowerCase();
      case 'stato':
        return intervento.stato || '';
      case 'capocantiere':
        return (intervento.capocantiere || '').toLowerCase();
      default:
        return '';
    }
  };

  const sortedInterventi = [...filteredInterventi].sort((a, b) => {
    const va = getSortValue(a, orderBy);
    const vb = getSortValue(b, orderBy);
    if (va < vb) return order === 'asc' ? -1 : 1;
    if (va > vb) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedInterventi = sortedInterventi.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // #4: il rapportino si compila ESCLUSIVAMENTE passando dalle timbrature.
  // Non apriamo più il modale inline: portiamo alla pagina Timbrature dell'intervento,
  // dove il rapportino diventa compilabile solo dopo aver timbrato la FINE LAVORO.
  const handleCompilaRapportino = (intervento) => {
    navigate('/timbrature', { state: { interventoId: intervento.id } });
  };

  // Statistiche
  const stats = {
    totali: interventi.length,
    programmati: interventi.filter(i => i.stato === 'programmato').length,
    inCorso: interventi.filter(i => i.stato === 'in_corso').length,
    completati: interventi.filter(i => i.stato === 'completato').length,
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'amministratore';
  // #5: l'amministratore NON compila i rapportini (solo capocantiere e admin/super_admin)
  const canCompilareRapportini = user?.role === 'capocantiere' || user?.role === 'admin' || user?.role === 'super_admin';
  // Il capocantiere crea interventi solo se l'admin gli ha attivato il permesso (Gestione Utenti).
  // Modifica ed eliminazione restano comunque all'ufficio (gate `isAdmin`).
  const isCapocantiere = user?.role === 'capocantiere';
  const canCreaInterventi = isAdmin || (isCapocantiere && !!user?.puo_creare_interventi);
  // Avviso: se un capocantiere assegna l'intervento a un collega, sparisce dalla sua lista
  const assegnaAdAltroCapo =
    isCapocantiere && !!formData.capocantiere_id && formData.capocantiere_id !== user?.id;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Skeleton variant="rounded" height={200} sx={{ mb: 3, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={60} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={400} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  return (
    <Box sx={{ pb: { xs: 10, sm: 4 } }}>
      {/* Header moderno con sfondo scuro */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          color: 'white',
          pt: { xs: 2, sm: 4 },
          pb: { xs: 3, sm: 4 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Pattern decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Accento rosso decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            background: 'radial-gradient(circle, rgba(229,0,0,0.15) 0%, transparent 70%)',
            borderRadius: '50%',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Titolo e bottone */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  bgcolor: '#e50000',
                  boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
                }}
              >
                <AssignmentIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    letterSpacing: '-0.5px',
                  }}
                >
                  {isAdmin ? 'Gestione Interventi' : 'I Miei Interventi'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                  {filteredInterventi.length} interventi {filterCliente || filterCantiere || filterCapocantiere || filterTecnico ? 'filtrati' : 'totali'}
                </Typography>
              </Box>
            </Box>

            {canCreaInterventi && !isMobile && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  bgcolor: '#e50000',
                  px: 3,
                  py: 1.2,
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
                  '&:hover': {
                    bgcolor: '#c50000',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(229,0,0,0.5)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Nuovo Intervento
              </Button>
            )}
          </Box>

          {/* Statistiche */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: { xs: 2, sm: 3 },
              mt: 3,
            }}
          >
            <StatBox
              icon={<AssignmentIcon />}
              value={stats.totali}
              label="Totali"
            />
            <StatBox
              icon={<CalendarIcon />}
              value={stats.programmati}
              label="Programmati"
            />
            <StatBox
              icon={<PlayArrowIcon />}
              value={stats.inCorso}
              label="In Corso"
            />
            <StatBox
              icon={<CheckCircleIcon />}
              value={stats.completati}
              label="Completati"
            />
          </Box>
        </Container>
      </Box>

      {/* Contenuto principale */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Barra di ricerca e filtri */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: searchFocused ? '0 4px 20px rgba(229,0,0,0.1)' : '0 2px 12px rgba(0,0,0,0.04)',
            transition: 'all 0.3s ease',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth
              placeholder="Cerca per cantiere, cliente, capocantiere..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: searchFocused ? '#e50000' : 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  '&:hover': { bgcolor: 'grey.100' },
                  '&.Mui-focused': {
                    bgcolor: 'white',
                    '& fieldset': { borderColor: '#e50000', borderWidth: 2 },
                  },
                },
              }}
            />

            {isAdmin && (
              <Button
                variant={showFilters ? 'contained' : 'outlined'}
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  minWidth: { xs: '100%', sm: 'auto' },
                  borderRadius: 2,
                  borderColor: showFilters ? '#e50000' : 'divider',
                  bgcolor: showFilters ? '#e50000' : 'transparent',
                  color: showFilters ? 'white' : 'text.primary',
                  '&:hover': {
                    borderColor: '#e50000',
                    bgcolor: showFilters ? '#c50000' : 'rgba(229,0,0,0.05)',
                  },
                }}
              >
                Filtri
              </Button>
            )}
          </Box>

          {/* Ordinamento e filtro rapido (TUTTI i ruoli) — servono soprattutto nella
              vista a schede, dove non ci sono gli header di tabella ordinabili */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
            <Chip
              icon={<CalendarIcon sx={{ fontSize: 18 }} />}
              label="Oggi"
              clickable
              onClick={() => setFilterOggi((v) => !v)}
              variant={filterOggi ? 'filled' : 'outlined'}
              sx={
                filterOggi
                  ? { bgcolor: '#e50000', color: 'white', fontWeight: 700, '& .MuiChip-icon': { color: 'white' }, '&:hover': { bgcolor: '#c50000' } }
                  : { fontWeight: 600 }
              }
            />

            {isCompact && (
              <>
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Ordina per data:
                </Typography>
                <Chip
                  label="↓ Più recenti"
                  clickable
                  onClick={() => { setOrderBy('data'); setOrder('desc'); }}
                  variant={orderBy === 'data' && order === 'desc' ? 'filled' : 'outlined'}
                  sx={
                    orderBy === 'data' && order === 'desc'
                      ? { bgcolor: '#e50000', color: 'white', fontWeight: 700, '&:hover': { bgcolor: '#c50000' } }
                      : { fontWeight: 600 }
                  }
                />
                <Chip
                  label="↑ Meno recenti"
                  clickable
                  onClick={() => { setOrderBy('data'); setOrder('asc'); }}
                  variant={orderBy === 'data' && order === 'asc' ? 'filled' : 'outlined'}
                  sx={
                    orderBy === 'data' && order === 'asc'
                      ? { bgcolor: '#e50000', color: 'white', fontWeight: 700, '&:hover': { bgcolor: '#c50000' } }
                      : { fontWeight: 600 }
                  }
                />
              </>
            )}
          </Box>

          {/* Filtri avanzati */}
          {isAdmin && showFilters && (
            <Fade in={showFilters}>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={clienti}
                    getOptionLabel={(option) => option.ragione_sociale || ''}
                    value={filterCliente}
                    onChange={(event, newValue) => setFilterCliente(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Filtra per Cliente"
                        placeholder="Cerca cliente..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={cantieri}
                    getOptionLabel={(option) => `${option.codice} - ${option.nome} (${option.citta})`}
                    value={filterCantiere}
                    onChange={(event, newValue) => setFilterCantiere(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Filtra per Cantiere"
                        placeholder="Cerca cantiere..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={capocantieri}
                    getOptionLabel={(option) => `${option.nome} ${option.cognome}`}
                    value={filterCapocantiere}
                    onChange={(event, newValue) => setFilterCapocantiere(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Filtra per Capocantiere"
                        placeholder="Cerca capocantiere..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={tecnici}
                    getOptionLabel={(option) => `${option.nome} ${option.cognome}`}
                    value={filterTecnico}
                    onChange={(event, newValue) => setFilterTecnico(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Filtra per Tecnico"
                        placeholder="Cerca tecnico..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                  />
                </Grid>
              </Grid>
            </Fade>
          )}
        </Paper>

        {/* Lista interventi */}
        {filteredInterventi.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <AssignmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm || filterCliente || filterCantiere || filterCapocantiere || filterTecnico || filterOggi
                ? 'Nessun intervento trovato'
                : 'Nessun intervento disponibile'}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {searchTerm || filterCliente || filterCantiere || filterCapocantiere || filterTecnico || filterOggi
                ? 'Prova a modificare i criteri di ricerca'
                : 'Crea il primo intervento per iniziare'}
            </Typography>
          </Paper>
        ) : isCompact ? (
          /* Vista Mobile - Cards */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {paginatedInterventi.map((intervento) => {
              const statoStyle = getStatoColor(intervento.stato);
              const tipologiaStyle = getTipologiaStyle(intervento.tipologia);
              const canCompila = (isAdmin || user?.role === 'capocantiere') && !intervento.has_rapportino;

              return (
                <Card
                  key={intervento.id}
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#e50000',
                      boxShadow: '0 8px 24px rgba(229,0,0,0.12)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    {/* Header card */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: tipologiaStyle.bg,
                            color: tipologiaStyle.color,
                          }}
                        >
                          <BuildIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                color: '#1a1a1a',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {intervento.cantiere}
                            </Typography>
                            {intervento.ricorrente && (
                              <Tooltip title="Intervento ricorrente">
                                <RepeatIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {intervento.cliente}
                          </Typography>
                        </Box>
                      </Box>

                      <Chip
                        icon={getStatoIcon(intervento.stato)}
                        label={intervento.stato.replace('_', ' ')}
                        size="small"
                        sx={{
                          bgcolor: statoStyle.bg,
                          color: statoStyle.color,
                          border: `1px solid ${statoStyle.border}`,
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          '& .MuiChip-icon': { color: statoStyle.color },
                        }}
                      />
                    </Box>

                    {/* Info intervento */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(intervento.data).format('DD/MM/YYYY')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {intervento.ora_inizio} - {intervento.ora_fine}
                        </Typography>
                      </Box>
                      {intervento.targa_mezzo && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {intervento.targa_mezzo}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Chips */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Chip
                        label={intervento.tipologia}
                        size="small"
                        sx={{
                          bgcolor: tipologiaStyle.bg,
                          color: tipologiaStyle.color,
                          fontWeight: 600,
                          textTransform: 'capitalize',
                        }}
                      />
                      {intervento.capocantiere && (
                        <Chip
                          icon={<PersonIcon sx={{ fontSize: '14px !important' }} />}
                          label={intervento.capocantiere}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                      {intervento.has_rapportino && (
                        <Chip
                          icon={<DescriptionIcon sx={{ fontSize: '14px !important' }} />}
                          label="Rapportino"
                          size="small"
                          sx={{
                            bgcolor: 'rgba(76, 175, 80, 0.1)',
                            color: '#4caf50',
                            fontWeight: 600,
                          }}
                        />
                      )}
                      {intervento.tempo_viaggio_andata && (
                        <Chip
                          icon={<TimerIcon sx={{ fontSize: '14px !important' }} />}
                          label={`${intervento.tempo_viaggio_andata} min`}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                    </Box>

                    {/* Azioni */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        pt: 2,
                        mt: 1,
                      }}
                    >
                      {canCompila && (
                        <IconButton
                          size="small"
                          onClick={() => handleCompilaRapportino(intervento)}
                          sx={{
                            bgcolor: 'rgba(255, 152, 0, 0.12)',
                            color: '#ff9800',
                            '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.2)' },
                          }}
                        >
                          <AssignmentIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                      {(isAdmin || user?.role === 'capocantiere') && (
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/interventi/${intervento.id}`)}
                          sx={{
                            bgcolor: 'rgba(229, 0, 0, 0.1)',
                            color: '#e50000',
                            '&:hover': { bgcolor: 'rgba(229, 0, 0, 0.2)' },
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleGetDirections(intervento)}
                        sx={{
                          bgcolor: 'rgba(33, 150, 243, 0.1)',
                          color: '#2196f3',
                          '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.2)' },
                        }}
                      >
                        <DirectionsIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      {isAdmin && (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteDialog(intervento)}
                          sx={{
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            color: '#f44336',
                            '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.2)' },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          /* Vista Desktop - Tabella */
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflowX: 'auto',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  {[
                    { id: 'data', label: 'Data' },
                    { id: 'cliente', label: 'Cliente' },
                    { id: 'cantiere', label: 'Cantiere' },
                    { id: 'tipologia', label: 'Tipologia' },
                    { id: 'mezzo', label: 'Mezzo' },
                    { id: 'stato', label: 'Stato' },
                    { id: 'capocantiere', label: 'Capocantiere' },
                  ].map((col) => (
                    <TableCell
                      key={col.id}
                      sortDirection={orderBy === col.id ? order : false}
                      sx={{ fontWeight: 700, color: 'text.secondary', py: 1.5, whiteSpace: 'nowrap' }}
                    >
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : 'asc'}
                        onClick={() => handleRequestSort(col.id)}
                        sx={{
                          '&.Mui-active': { color: '#e50000' },
                          '&.Mui-active .MuiTableSortLabel-icon': { color: '#e50000 !important' },
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', py: 1.5, whiteSpace: 'nowrap' }}>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInterventi.map((intervento) => {
                  const statoStyle = getStatoColor(intervento.stato);
                  const tipologiaStyle = getTipologiaStyle(intervento.tipologia);
                  const canCompila = canCompilareRapportini && !intervento.has_rapportino;

                  return (
                    <TableRow
                      key={intervento.id}
                      sx={{
                        '&:hover': { bgcolor: 'rgba(229, 0, 0, 0.02)' },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell sx={{ py: 1.5 }}>
                        <Tooltip title={canCompila ? 'Compila rapportino' : ''} disableHoverListener={!canCompila}>
                          <Box
                            onClick={canCompila ? () => handleCompilaRapportino(intervento) : undefined}
                            sx={canCompila ? {
                              cursor: 'pointer',
                              '&:hover .data-compila': { color: '#e50000', textDecoration: 'underline' },
                            } : {}}
                          >
                            <Typography className="data-compila" variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                              {dayjs(intervento.data).format('DD/MM/YYYY')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {intervento.ora_inizio} - {intervento.ora_fine}
                              {intervento.ricorrente && ' 🔄'}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{intervento.cliente}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', color: '#e50000' }}>
                          {intervento.cantiere}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={intervento.tipologia === 'manutenzione' ? 'Manut.' : 'Impian.'}
                          size="small"
                          sx={{
                            bgcolor: tipologiaStyle.bg,
                            color: tipologiaStyle.color,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {intervento.targa_mezzo || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={intervento.stato.replace('_', ' ')}
                            size="small"
                            sx={{
                              bgcolor: statoStyle.bg,
                              color: statoStyle.color,
                              border: `1px solid ${statoStyle.border}`,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 22,
                              textTransform: 'capitalize',
                            }}
                          />
                          {intervento.has_rapportino && (
                            <Tooltip title="Rapportino compilato">
                              <DescriptionIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{intervento.capocantiere || '-'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          {canCompila && (
                            <Tooltip title="Compila rapportino">
                              <IconButton
                                size="small"
                                onClick={() => handleCompilaRapportino(intervento)}
                                sx={{
                                  color: '#ff9800',
                                  '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.1)' },
                                }}
                              >
                                <AssignmentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(isAdmin || user?.role === 'capocantiere') && (
                            <Tooltip title="Visualizza dettaglio">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/interventi/${intervento.id}`)}
                                sx={{
                                  color: '#e50000',
                                  '&:hover': { bgcolor: 'rgba(229, 0, 0, 0.1)' },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Indicazioni stradali">
                            <IconButton
                              size="small"
                              onClick={() => handleGetDirections(intervento)}
                              sx={{
                                color: '#2196f3',
                                '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.1)' },
                              }}
                            >
                              <DirectionsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isAdmin && (
                            <Tooltip title="Elimina">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDeleteDialog(intervento)}
                                sx={{
                                  color: '#f44336',
                                  '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Paginazione */}
        {filteredInterventi.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <TablePagination
              component="div"
              count={filteredInterventi.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Righe per pagina:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
              sx={{
                bgcolor: '#fafafa',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Paper>
        )}
      </Container>

      {/* FAB mobile per nuovo intervento */}
      {canCreaInterventi && isMobile && (
        <Fab
          color="primary"
          onClick={() => handleOpenDialog()}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            bgcolor: '#e50000',
            boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
            '&:hover': {
              bgcolor: '#c50000',
            },
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialog Nuovo/Modifica Intervento */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header Dialog */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            color: 'white',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: '#e50000' }}>
                {currentIntervento ? <BuildIcon /> : (duplicating ? <ContentCopyIcon /> : <AddIcon />)}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {currentIntervento ? 'Modifica Intervento' : (duplicating ? 'Duplica Intervento' : 'Nuovo Intervento')}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  {currentIntervento ? 'Modifica i dati dell\'intervento' : (duplicating ? 'Copia di un intervento esistente — scegli la nuova data' : 'Compila i campi per creare un nuovo intervento')}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {duplicating && (
            <Alert severity="info" icon={<ContentCopyIcon />} sx={{ mb: 3, borderRadius: 2 }}>
              Stai creando una <strong>copia</strong>: scegli la nuova data, oppure spunta
              "Intervento Ricorrente" per generare una serie. L'intervento originale resta invariato.
            </Alert>
          )}

          {!currentIntervento && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isRicorrente}
                  onChange={(e) => setIsRicorrente(e.target.checked)}
                  sx={{
                    color: '#e50000',
                    '&.Mui-checked': { color: '#e50000' },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RepeatIcon sx={{ fontSize: 20 }} />
                  <Typography>Intervento Ricorrente</Typography>
                </Box>
              }
              sx={{ mb: 3 }}
            />
          )}

          <Grid container spacing={2.5}>
            {isRicorrente ? (
              <>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Data Inizio"
                    value={formData.data_inizio}
                    onChange={handleDateChange('data_inizio')}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Data Fine"
                    value={formData.data_fine}
                    onChange={handleDateChange('data_fine')}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                      Giorni della Settimana
                    </FormLabel>
                    <FormGroup row>
                      {giorniSettimana.map((giorno) => (
                        <FormControlLabel
                          key={giorno.value}
                          control={
                            <Checkbox
                              checked={formData.giorni_settimana.includes(giorno.value)}
                              onChange={() => handleGiorniChange(giorno.value)}
                              sx={{
                                color: '#e50000',
                                '&.Mui-checked': { color: '#e50000' },
                              }}
                            />
                          }
                          label={giorno.label}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <DatePicker
                  label="Data"
                  value={formData.data}
                  onChange={handleDateChange('data')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                    },
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TimePicker
                label="Ora Inizio"
                value={formData.ora_inizio}
                onChange={handleDateChange('ora_inizio')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TimePicker
                label="Ora Fine"
                value={formData.ora_fine}
                onChange={handleDateChange('ora_fine')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                name="tipologia"
                label="Tipologia"
                fullWidth
                required
                value={formData.tipologia}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                <MenuItem value="manutenzione">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BuildIcon sx={{ fontSize: 18, color: '#e50000' }} />
                    Manutenzione
                  </Box>
                </MenuItem>
                <MenuItem value="impianto">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BuildIcon sx={{ fontSize: 18, color: '#9c27b0' }} />
                    Impianto
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                name="stato"
                label="Stato"
                fullWidth
                required
                value={formData.stato}
                onChange={handleInputChange}
                disabled={!currentIntervento}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                <MenuItem value="programmato">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon sx={{ fontSize: 18, color: '#2196f3' }} />
                    Programmato
                  </Box>
                </MenuItem>
                <MenuItem value="in_corso">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PlayArrowIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                    In Corso
                  </Box>
                </MenuItem>
                <MenuItem value="completato">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                    Completato
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={cantieri}
                getOptionLabel={(option) => `${option.codice} - ${option.nome} (${option.citta})`}
                value={cantieri.find(c => c.id === formData.cantiere_id) || null}
                onChange={handleCantiereChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cantiere"
                    required
                    fullWidth
                    placeholder="Cerca cantiere..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="cliente_id"
                label="Cliente"
                fullWidth
                required
                value={clienti.find(c => c.id === formData.cliente_id)?.ragione_sociale || ''}
                InputProps={{
                  readOnly: true,
                }}
                helperText="Compilato automaticamente in base al cantiere"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={capocantieri}
                getOptionLabel={(option) => `${option.nome} ${option.cognome}`}
                value={capocantieri.find(c => c.id === formData.capocantiere_id) || null}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    capocantiere_id: newValue ? newValue.id : '',
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Capocantiere"
                    required
                    fullWidth
                    placeholder="Cerca capocantiere..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
              {assegnaAdAltroCapo && (
                <Alert severity="warning" sx={{ mt: 1, borderRadius: 2, py: 0.5 }}>
                  Stai assegnando l'intervento a un altro capocantiere: sarà lui a gestirlo sul campo e
                  l'intervento <strong>non comparirà più nella tua lista</strong>.
                </Alert>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                options={tecnici}
                getOptionLabel={(option) => `${option.nome} ${option.cognome}`}
                value={tecnici.filter(t => formData.tecnici_ids.includes(t.id))}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    tecnici_ids: newValue.map(t => t.id),
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tecnici"
                    fullWidth
                    placeholder="Cerca tecnici..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={isRicorrente ? veicoli : veicoliDisponibili}
                getOptionLabel={(option) => `${option.targa} - ${option.modello}`}
                value={(isRicorrente ? veicoli : veicoliDisponibili).find(v => v.targa === formData.targa_mezzo) || null}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    targa_mezzo: newValue ? newValue.targa : '',
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Veicolo"
                    fullWidth
                    placeholder={isRicorrente ? "Seleziona veicolo..." : (formData.data ? "Seleziona veicolo..." : "Seleziona prima la data")}
                    helperText={
                      isRicorrente
                        ? `${veicoli.length} veicolo/i disponibile/i — verrà assegnato a tutte le occorrenze`
                        : !formData.data
                          ? "Seleziona una data per vedere i veicoli disponibili"
                          : veicoliDisponibili.length === 0
                            ? "Nessun veicolo disponibile per questa data"
                            : `${veicoliDisponibili.length} veicolo/i disponibile/i`
                    }
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.targa === value?.targa}
                disabled={isRicorrente ? false : !formData.data}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.abilita_timbrature_viaggio}
                    onChange={(e) => setFormData(prev => ({ ...prev, abilita_timbrature_viaggio: e.target.checked }))}
                    sx={{ color: '#e50000', '&.Mui-checked': { color: '#e50000' } }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CarIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2">Includi timbrature viaggio</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Disabilita se la squadra è già sul posto
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="descrizione_lavori"
                label="Descrizione Lavori"
                fullWidth
                required
                multiline
                rows={3}
                value={formData.descrizione_lavori}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="note_operative"
                label="Note Operative"
                fullWidth
                multiline
                rows={2}
                value={formData.note_operative}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          {currentIntervento && (
            <Button
              onClick={handleDuplica}
              startIcon={<ContentCopyIcon />}
              sx={{ mr: 'auto', borderRadius: 2, px: 3, color: '#e50000' }}
            >
              Duplica
            </Button>
          )}
          <Button
            onClick={handleCloseDialog}
            sx={{
              borderRadius: 2,
              px: 3,
            }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#e50000',
              borderRadius: 2,
              px: 4,
              fontWeight: 600,
              '&:hover': { bgcolor: '#c50000' },
            }}
          >
            {currentIntervento ? 'Salva Modifiche' : 'Crea Intervento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' },
        }}
      >
        <Box
          sx={{
            bgcolor: '#f44336',
            color: 'white',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
              <DeleteIcon />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Conferma Eliminazione
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Typography>
            Sei sicuro di voler eliminare l'intervento del{' '}
            <strong>{interventoToDelete?.data ? dayjs(interventoToDelete.data).format('DD/MM/YYYY') : ''}</strong>{' '}
            presso <strong>{interventoToDelete?.cantiere}</strong>?
          </Typography>
          {interventoToDelete?.ricorrente && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
              Questo è un intervento ricorrente. Puoi scegliere di eliminare solo questo o tutti gli interventi della serie.
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseDeleteDialog} sx={{ borderRadius: 2 }}>
            Annulla
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={{
              bgcolor: '#f44336',
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              '&:hover': { bgcolor: '#d32f2f' },
            }}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modale compilazione rapportino: apertura diretta dalla lista interventi,
          senza passare dal calendario */}
      {rapportinoIntervento && (
        <RapportinoModal
          open={rapportinoModalOpen}
          onClose={() => setRapportinoModalOpen(false)}
          intervento={rapportinoIntervento}
          timbrature={rapportinoTimbrature}
          presenze={rapportinoPresenze}
          onSuccess={() => {
            setRapportinoModalOpen(false);
            loadData();
          }}
        />
      )}
    </Box>
  );
};

export default Interventi;
